import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Restores the lowercase-path enforcement the old Next.js middleware provided
 * (removed in the Vite/React Router migration). Mixed-case URLs like `/AR/Login`
 * are redirected to their lowercase form so legacy/external links resolve
 * instead of falling through to the 404 route.
 *
 * Case-sensitive tokens are preserved — only locale and static route segments
 * are lowercased:
 *  - the payment receipt token in `/:lng/receipt/:reference`
 *  - the short-url token in `/SL/:token` (resolved by ShortUrlRedirect)
 *
 * Render as a sibling of `<Routes>` inside `<BrowserRouter>` so it observes
 * every location change regardless of which route matches.
 */
export default function LowercasePathRedirect() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const segments = pathname.split("/");
    // Index of the first segment whose original case must be preserved (the
    // token) — everything from here on is left untouched.
    // Shapes: ["", "sl", "{token}"] and ["", "{lng}", "receipt", "{reference}"].
    let preserveFrom = Infinity;
    if (segments[1]?.toLowerCase() === "sl") {
      preserveFrom = 2;
    } else if (segments[2]?.toLowerCase() === "receipt") {
      preserveFrom = 3;
    }

    const lowered = segments
      .map((segment, index) =>
        index >= preserveFrom ? segment : segment.toLowerCase(),
      )
      .join("/");

    if (lowered !== pathname) {
      navigate(`${lowered}${search}`, { replace: true });
    }
  }, [pathname, search, navigate]);

  return null;
}
